/**
 * Create a new capability bus instance.
 *
 * Returns the bus and its internal state separately. The caller is responsible
 * for treating the bus as a singleton — call this once in your host app and
 * pass the result down to remotes via `RemotePageMountOptions`.
 *
 * @example
 * ```typescript
 * // In your host app's service module:
 * import { createCapabilityBus } from '@platform-ui/remote-utils';
 *
 * const { bus } = createCapabilityBus({ devTools: import.meta.env.DEV });
 * export const capabilityBus = bus;
 * ```
 */
export function createCapabilityBus(options = {}) {
    const services = new Map();
    const eventListeners = new Map();
    const pendingWaiters = new Map();
    const bus = {
        registerService(key, service, options) {
            if (options?.persistent && services.has(key)) {
                return;
            }
            services.set(key, service);
            // Resolve any pending waitForService promises
            const waiters = pendingWaiters.get(key);
            if (waiters) {
                for (const resolve of waiters) {
                    resolve(service);
                }
                pendingWaiters.delete(key);
            }
        },
        getService(key) {
            return services.get(key);
        },
        waitForService(key) {
            const existing = services.get(key);
            if (existing !== undefined) {
                return Promise.resolve(existing);
            }
            return new Promise(resolve => {
                const waiters = pendingWaiters.get(key) ?? [];
                waiters.push(resolve);
                pendingWaiters.set(key, waiters);
            });
        },
        publish(event) {
            const listeners = eventListeners.get(event.type);
            if (!listeners)
                return;
            for (const handler of listeners) {
                handler(event.payload);
            }
        },
        subscribe(eventType, handler) {
            const listeners = eventListeners.get(eventType) ?? new Set();
            listeners.add(handler);
            eventListeners.set(eventType, listeners);
            return () => {
                listeners.delete(handler);
                if (listeners.size === 0) {
                    eventListeners.delete(eventType);
                }
            };
        }
    };
    if (options.devTools && typeof window !== 'undefined') {
        window.__PORTAL_CAPABILITY_BUS__ = {
            getServices: () => Array.from(services.keys()),
            getService: (key) => bus.getService(key),
            getEventTypes: () => Array.from(eventListeners.keys()),
            getSubscriberCount: (type) => eventListeners.get(type)?.size ?? 0
        };
    }
    return { bus, internals: { services, eventListeners } };
}
//# sourceMappingURL=capability-bus.js.map